/**
 * Season-pass helpers for multi-city / grouped booking.
 * Season passes are ticket rows with is_season_pass on child events;
 * the same ticket id repeating across legs = events covered by that pass.
 */

function isCityBucket(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Object.values(value).some(
    (venue) =>
      venue &&
      typeof venue === "object" &&
      !Array.isArray(venue) &&
      venue.dates &&
      typeof venue.dates === "object",
  );
}

/** Iterate city → venue → date → events from a Booking group payload. */
export function forEachGroupedEvent(data, visitor) {
  if (!data || typeof data !== "object") return;

  const roots = [];
  if (Array.isArray(data.grouped_data)) {
    data.grouped_data.forEach((chunk) => {
      if (chunk && typeof chunk === "object") roots.push(chunk);
    });
  }
  roots.push(data);
  if (data.parent_event && typeof data.parent_event === "object") {
    roots.push({ ...data.parent_event, ...(data.grouped_data ? {} : {}) });
  }

  const seen = new Set();

  roots.forEach((root) => {
    Object.entries(root || {}).forEach(([cityName, cityData]) => {
      if (!isCityBucket(cityData)) return;
      Object.entries(cityData).forEach(([venueKey, venue]) => {
        if (venueKey === "sold_out" || !venue || typeof venue !== "object") {
          return;
        }
        const dates = venue.dates;
        if (!dates || typeof dates !== "object") return;
        const venueLabel = venue.venue_name || venueKey;

        Object.entries(dates).forEach(([date, dateData]) => {
          (dateData?.events || []).forEach((ev) => {
            const childId = ev?.child_event_id || ev?.id || null;
            const dedupeKey = `${cityName}|${venueLabel}|${date}|${childId}|${ev?.start_time || ""}`;
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);
            visitor({
              city: cityName,
              venue: venueLabel,
              venueObject: venue,
              date,
              dateData,
              event: ev,
              childEventId: childId,
            });
          });
        });
      });
    });
  });
}

function isPassTicketActive(ticket) {
  if (!ticket?.is_season_pass) return false;
  const status = String(ticket?.status || "active").toLowerCase();
  if (["inactive", "disabled", "expired", "archived", "deleted"].includes(status)) {
    return false;
  }
  if (
    ticket?.is_sold_out === true ||
    ticket?.is_sold_out === 1 ||
    ticket?.is_sold_out === "1"
  ) {
    return false;
  }
  const available = Number(
    ticket?.available ?? ticket?.available_quantity ?? 0,
  );
  if (Number.isFinite(available) && available <= 0) return false;
  return true;
}

/**
 * Build a catalog of season passes found across the group,
 * each with every covered event/leg.
 */
export function buildSeasonPassCatalog(data) {
  const byId = new Map();

  forEachGroupedEvent(data, ({ city, venue, date, event, childEventId }) => {
    (event?.tickets || []).forEach((ticket) => {
      if (!ticket?.is_season_pass || !ticket?.id) return;

      if (!byId.has(ticket.id)) {
        byId.set(ticket.id, {
          id: ticket.id,
          ticket,
          coveredEvents: [],
        });
      }

      const entry = byId.get(ticket.id);
      // Prefer the freshest ticket payload (availability can differ by leg).
      if (isPassTicketActive(ticket)) {
        entry.ticket = ticket;
      }

      const already = entry.coveredEvents.some(
        (c) =>
          c.childEventId === childEventId &&
          c.date === date &&
          c.startTime === (event?.start_time || null),
      );
      if (already) return;

      entry.coveredEvents.push({
        childEventId,
        date,
        startTime: event?.start_time || null,
        endDate: event?.end_date || date,
        endTime: event?.end_time || null,
        timezone: event?.timezone || null,
        eventName:
          event?.name ||
          event?.child_event_name ||
          event?.event_name ||
          "Event",
        city,
        venue,
      });
    });
  });

  byId.forEach((entry) => {
    entry.coveredEvents.sort((a, b) => {
      const da = `${a.date} ${a.startTime || "00:00:00"}`;
      const db = `${b.date} ${b.startTime || "00:00:00"}`;
      return da.localeCompare(db);
    });
  });

  return byId;
}

/**
 * Active season passes matching the current booking selection.
 * Supports childEventId, date, venue, and/or city filters.
 * If only venue/city is set (Venue step), returns all active passes
 * that cover any event at that venue/city.
 */
export function getEligibleSeasonPasses(
  data,
  { childEventId, date, venue, city } = {},
) {
  const hasFilter = Boolean(childEventId || date || venue || city);
  if (!hasFilter) return [];

  const catalog = buildSeasonPassCatalog(data);
  const results = [];
  const venueKey = venue ? String(venue).toLowerCase() : null;
  const cityKey = city ? String(city).toLowerCase() : null;

  catalog.forEach((entry) => {
    if (!isPassTicketActive(entry.ticket)) return;

    const coversSelection = entry.coveredEvents.some((c) => {
      if (childEventId) {
        return c.childEventId === childEventId;
      }
      if (date) {
        return c.date === date;
      }
      if (venueKey) {
        const cVenue = String(c.venue || "").toLowerCase();
        return (
          cVenue === venueKey ||
          cVenue.includes(venueKey) ||
          venueKey.includes(cVenue)
        );
      }
      if (cityKey) {
        return String(c.city || "").toLowerCase() === cityKey;
      }
      return false;
    });
    if (!coversSelection) return;

    results.push({
      ...entry.ticket,
      coveredEvents: entry.coveredEvents,
      eventCount: entry.coveredEvents.length,
      validityStart: entry.coveredEvents[0]?.date || null,
      validityEnd:
        entry.coveredEvents[entry.coveredEvents.length - 1]?.endDate ||
        entry.coveredEvents[entry.coveredEvents.length - 1]?.date ||
        null,
    });
  });

  return results.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
}

export function hasEligibleSeasonPasses(data, selection) {
  return getEligibleSeasonPasses(data, selection).length > 0;
}

/**
 * Season passes that appear on any event under a venue's `dates` map
 * (used on the Venue step — reliable even when venue keys differ from labels).
 */
export function getSeasonPassesForVenueDates(data, venueDates) {
  if (!venueDates || typeof venueDates !== "object") return [];

  const catalog = buildSeasonPassCatalog(data);
  const ticketIds = new Set();

  Object.values(venueDates).forEach((dateData) => {
    (dateData?.events || []).forEach((ev) => {
      (ev?.tickets || []).forEach((t) => {
        if (t?.is_season_pass && t?.id && isPassTicketActive(t)) {
          ticketIds.add(t.id);
        }
      });
    });
  });

  const results = [];
  ticketIds.forEach((id) => {
    const entry = catalog.get(id);
    if (!entry || !isPassTicketActive(entry.ticket)) return;
    results.push({
      ...entry.ticket,
      coveredEvents: entry.coveredEvents,
      eventCount: entry.coveredEvents.length,
      validityStart: entry.coveredEvents[0]?.date || null,
      validityEnd:
        entry.coveredEvents[entry.coveredEvents.length - 1]?.endDate ||
        entry.coveredEvents[entry.coveredEvents.length - 1]?.date ||
        null,
    });
  });

  return results.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
}

export function hasSeasonPassesForVenueDates(data, venueDates) {
  return getSeasonPassesForVenueDates(data, venueDates).length > 0;
}

/** All active season passes across the whole multi-city event. */
export function getAllActiveSeasonPasses(data) {
  const catalog = buildSeasonPassCatalog(data);
  const results = [];
  catalog.forEach((entry) => {
    if (!isPassTicketActive(entry.ticket)) return;
    results.push({
      ...entry.ticket,
      coveredEvents: entry.coveredEvents,
      eventCount: entry.coveredEvents.length,
      validityStart: entry.coveredEvents[0]?.date || null,
      validityEnd:
        entry.coveredEvents[entry.coveredEvents.length - 1]?.endDate ||
        entry.coveredEvents[entry.coveredEvents.length - 1]?.date ||
        null,
    });
  });
  return results.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
}

export function hasAnyActiveSeasonPasses(data) {
  return getAllActiveSeasonPasses(data).length > 0;
}

/** True when the event has season passes but every one of them is sale-closed. */
export function areAllSeasonPassesClosed(data) {
  const catalog = buildSeasonPassCatalog(data);
  if (catalog.size === 0) return false;

  let hasAny = false;
  let allClosed = true;
  catalog.forEach((entry) => {
    hasAny = true;
    if (String(entry.ticket?.status || "").toLowerCase() !== "closed") {
      allClosed = false;
    }
  });
  return hasAny && allClosed;
}
